import { useState, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface LogoUploaderProps {
  value: string | null;
  onChange: (url: string | null) => void;
}

const MAX_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];

export function LogoUploader({ value, onChange }: LogoUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(value);

  const uploadMutation = trpc.settings.uploadLogo.useMutation({
    onSuccess: (data) => {
      onChange(data.url);
      setPreview(data.url);
      toast.success("Logo erfolgreich hochgeladen");
    },
    onError: (error) => {
      toast.error(error.message || "Fehler beim Hochladen");
    },
  });

  const handleFile = useCallback(async (file: File) => {
    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Ungültiges Dateiformat. Erlaubt: PNG, JPEG, SVG, WebP");
      return;
    }

    // Validate file size
    if (file.size > MAX_SIZE) {
      toast.error("Datei zu groß. Maximale Größe: 2MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Convert to base64 and upload
    const base64Reader = new FileReader();
    base64Reader.onload = async (e) => {
      const base64 = (e.target?.result as string).split(",")[1];
      
      uploadMutation.mutate({
        filename: file.name,
        contentType: file.type,
        base64Data: base64,
      });
    };
    base64Reader.readAsDataURL(file);
  }, [uploadMutation]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleRemove = useCallback(() => {
    setPreview(null);
    onChange(null);
  }, [onChange]);

  return (
    <div className="space-y-2">
      <Label>Logo (optional)</Label>
      
      {preview ? (
        <div className="relative w-full h-32 border rounded-lg overflow-hidden bg-muted/50">
          <img
            src={preview}
            alt="Logo preview"
            className="w-full h-full object-contain p-4"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
          {uploadMutation.isPending && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "relative w-full h-32 border-2 border-dashed rounded-lg transition-colors",
            "flex flex-col items-center justify-center gap-2 cursor-pointer",
            isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
            uploadMutation.isPending && "pointer-events-none opacity-50"
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => document.getElementById("logo-input")?.click()}
        >
          <input
            id="logo-input"
            type="file"
            accept={ALLOWED_TYPES.join(",")}
            onChange={handleInputChange}
            className="hidden"
          />
          
          {uploadMutation.isPending ? (
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          ) : (
            <>
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                {isDragging ? (
                  <Upload className="h-5 w-5 text-primary" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">
                  {isDragging ? "Logo hier ablegen" : "Logo hochladen"}
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPEG, SVG oder WebP (max. 2MB)
                </p>
              </div>
            </>
          )}
        </div>
      )}
      
      <p className="text-xs text-muted-foreground">
        Ihr Logo wird in der Navigation und auf Dokumenten angezeigt
      </p>
    </div>
  );
}
